import React, { useEffect, useState } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CUSTOM_FIELD_TYPES } from "@/components/CustomFields";
import { HeartHandshake, Pencil, Phone, Mail, Plus, Loader2, Trash2, Settings2 } from "lucide-react";
import { toast } from "sonner";

const empty = { nome: "", descrizione: "", responsabile_nome: "", telefono: "", whatsapp: "", email: "", attivo: true, pubblico: true, categorie: [], tipo_follow_up: "Appuntamento", campi_personalizzati: [] };

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || `campo_${Date.now()}`;
const FOLLOWUP_TYPES = ["Appuntamento", "Corso", "Attività"];

const Services = () => {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/services").then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openEdit = (s) => { setEdit(s); setF(s ? { ...empty, ...s } : empty); setOpen(true); };

  // --- gestione campi personalizzati ---
  const cf = Array.isArray(f.campi_personalizzati) ? f.campi_personalizzati : [];
  const setCf = (list) => setF((prev) => ({ ...prev, campi_personalizzati: list }));
  const addField = () => setCf([...cf, { chiave: "", label: "", tipo: "testo", opzioni: [], target: "pratica", ricercabile: true }]);
  const updField = (i, patch) => setCf(cf.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeField = (i) => setCf(cf.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!f.nome.trim()) { toast.error("Inserisci il nome del servizio"); return; }
    setSaving(true);
    const campi = cf
      .filter((x) => (x.label || "").trim())
      .map((x) => ({
        chiave: x.chiave || slugify(x.label),
        label: x.label.trim(),
        tipo: x.tipo || "testo",
        target: x.target || "pratica",
        ricercabile: x.ricercabile !== false,
        opzioni: x.tipo === "select"
          ? (typeof x.opzioni === "string" ? x.opzioni.split("\n").map((o) => o.trim()).filter(Boolean) : (x.opzioni || []))
          : [],
      }));
    const payload = { ...f, tipo_follow_up: f.tipo_follow_up || "Appuntamento", campi_personalizzati: campi,
      categorie: typeof f.categorie === "string" ? f.categorie.split("\n").map((x) => x.trim()).filter(Boolean) : f.categorie };
    try {
      if (edit) await api.put(`/services/${edit.id}`, payload); else await api.post("/services", payload);
      toast.success("Servizio salvato"); setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader icon={HeartHandshake} title="Servizi" description="Servizi configurabili della Rete (modificabili senza intervenire nel codice)"
        actions={can("services.write") && <Button onClick={() => openEdit(null)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"><Plus className="h-4 w-4 mr-2" /> Nuovo servizio</Button>} />
      {loading ? <LoadingState /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((s) => (
            <Card key={s.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-semibold">{s.nome}</h3>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] rounded-full px-2 py-0.5 ${s.attivo ? "bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]" : "bg-slate-100 text-slate-500"}`}>{s.attivo ? "Attivo" : "Inattivo"}</span>
                  {can("services.write") && <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button>}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{s.descrizione}</p>
              <div className="text-xs mt-2 space-y-0.5">
                {s.responsabile_nome && <div>Responsabile: <span className="font-medium">{s.responsabile_nome}</span></div>}
                <div className="flex flex-wrap gap-x-3 text-muted-foreground">{s.telefono && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{s.telefono}</span>}{s.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span>}</div>
              </div>
              {s.categorie?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{s.categorie.map((c) => <span key={c} className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5">{c}</span>)}</div>}
              {s.campi_personalizzati?.length > 0 && (
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-[color:var(--brand-blue-700)] bg-[color:var(--brand-blue-100)] rounded-full px-2 py-0.5">
                  <Settings2 className="h-3 w-3" /> {s.campi_personalizzati.length} campo/i personalizzato/i
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-auto ras-scroll">
          <DialogHeader><DialogTitle className="font-display">{edit ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} /></div>
            <div><Label>Descrizione</Label><Textarea value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Responsabile</Label><Input value={f.responsabile_nome} onChange={(e) => setF({ ...f, responsabile_nome: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} /></div>
              <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            </div>
            <div><Label>Categorie / corsi (una per riga)</Label><Textarea value={Array.isArray(f.categorie) ? f.categorie.join("\n") : f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })} rows={3} /></div>
            <div><Label>Tipo di follow-up predefinito (proposto alla presa in carico)</Label>
              <Select value={f.tipo_follow_up || "Appuntamento"} onValueChange={(v) => setF({ ...f, tipo_follow_up: v })}>
                <SelectTrigger data-testid="service-followup-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>

            <div className="rounded-lg border p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-1.5"><Settings2 className="h-4 w-4 text-[color:var(--brand-blue-600)]" /> Campi personalizzati</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField} data-testid="add-custom-field-button"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi campo</Button>
              </div>
              {cf.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nessun campo aggiuntivo. Puoi aggiungere campi extra (testo, numero, data, elenco, sì/no) per Pratica o Persona: saranno ricercabili ed esportabili.</p>
              ) : (
                <div className="space-y-3">
                  {cf.map((x, i) => (
                    <div key={i} className="rounded-md border bg-white p-2.5 space-y-2" data-testid={`custom-field-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <Input placeholder="Etichetta campo (es. Codice ISEE)" value={x.label} onChange={(e) => updField(i, { label: e.target.value })} className="h-9" data-testid={`custom-field-label-${i}`} />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeField(i)} className="text-[color:var(--brand-red-600)] shrink-0" data-testid={`remove-custom-field-${i}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={x.tipo || "testo"} onValueChange={(v) => updField(i, { tipo: v })}>
                          <SelectTrigger className="h-9" data-testid={`custom-field-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{CUSTOM_FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={x.target || "pratica"} onValueChange={(v) => updField(i, { target: v })}>
                          <SelectTrigger className="h-9" data-testid={`custom-field-target-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pratica">Sulla Pratica</SelectItem>
                            <SelectItem value="persona">Sulla Persona</SelectItem>
                            <SelectItem value="entrambe">Su entrambe</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {x.tipo === "select" && (
                        <Textarea placeholder="Opzioni (una per riga)" rows={2}
                          value={Array.isArray(x.opzioni) ? x.opzioni.join("\n") : (x.opzioni || "")}
                          onChange={(e) => updField(i, { opzioni: e.target.value })} data-testid={`custom-field-options-${i}`} />
                      )}
                      <div className="flex items-center gap-2">
                        <Switch checked={x.ricercabile !== false} onCheckedChange={(v) => updField(i, { ricercabile: v })} data-testid={`custom-field-searchable-${i}`} />
                        <span className="text-xs text-muted-foreground">Ricercabile ed esportabile</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={f.attivo} onCheckedChange={(v) => setF({ ...f, attivo: v })} /><Label>Attivo</Label></div>
              <div className="flex items-center gap-2"><Switch checked={f.pubblico} onCheckedChange={(v) => setF({ ...f, pubblico: v })} /><Label>Visibile nel portale pubblico</Label></div>
            </div>
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Services;
